import { useLang } from "@/contexts/LangContext";
import { useLongPress } from "@/components/LongPressMenu";
import { colorIdToHex } from "@/lib/highlightColors";
import { BookOpen, ArrowRight } from "@phosphor-icons/react";

/**
 * Presentational card for the Verse of the Day.
 * Pure view layer — receives the already-resolved verse plus a single
 * `onLongPress` callback. The long-press enters multi-row selection on
 * the parent page so the unified `SelectionActionBar` (Copy / Share /
 * 5 colour swatches) can take over — same surface used by Bible,
 * Catechism, Prayers cards.
 */
export function VerseOfTheDayCard({
    reference,
    text,
    savedColor,
    isSaved,
    isSelected,
    onLongPress,
    onOpenBible,
}) {
    const { t } = useLang();
    const longPress = useLongPress(onLongPress);
    const bg = colorIdToHex(savedColor);

    return (
        <section
            className={[
                "surface-card p-6 sm:p-8 relative overflow-hidden select-none transition-colors",
                isSelected
                    ? "underline decoration-blue-600 decoration-2 underline-offset-4"
                    : "",
                isSaved && !bg ? "border-l-2 border-sangre/50" : "",
            ].join(" ")}
            data-testid="votd-card"
            data-selected={isSelected ? "true" : "false"}
            data-color={savedColor || ""}
            aria-label={t("votd.title")}
            style={{
                WebkitUserSelect: "none",
                WebkitTouchCallout: "none",
                backgroundColor: bg || undefined,
            }}
            {...longPress}
        >
            <span
                aria-hidden="true"
                className="absolute -top-4 -right-4 text-sangre/10 pointer-events-none"
            >
                <BookOpen size={140} weight="duotone" />
            </span>

            <div className="relative flex items-start justify-between gap-4 flex-wrap mb-4">
                <div className="min-w-0">
                    <p className="label-eyebrow text-sangre mb-1">{t("votd.title")}</p>
                    <h2
                        className="heading-serif text-2xl sm:text-3xl tracking-tight leading-tight m-0"
                        data-testid="votd-reference"
                    >
                        {reference}
                    </h2>
                </div>
            </div>

            <blockquote
                className="reading-serif text-lg sm:text-xl text-stone900 leading-[1.65] italic border-l-2 border-sangre/50 pl-4 my-6"
                data-testid="votd-text"
            >
                &ldquo;{text}&rdquo;
            </blockquote>

            <div
                className="flex items-center gap-2 relative"
                data-testid="votd-actions"
            >
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onOpenBible(); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    data-testid="votd-open-bible"
                    className="inline-flex items-center gap-1.5 px-4 py-2 ui-sans text-xs uppercase tracking-widest font-semibold rounded-md bg-sangre text-sand-50 hover:bg-sangre-hover transition-colors"
                >
                    <span>{t("votd.open_bible")}</span>
                    <ArrowRight size={14} weight="bold" />
                </button>
            </div>
        </section>
    );
}

export function VerseOfTheDaySkeleton() {
    return (
        <section
            className="surface-card p-6 sm:p-8 animate-pulse"
            data-testid="votd-loading"
            aria-hidden="true"
        >
            <div className="h-3 w-24 bg-sand-200 rounded mb-4" />
            <div className="h-6 w-48 bg-sand-200 rounded mb-6" />
            <div className="h-4 w-full bg-sand-200 rounded mb-2" />
            <div className="h-4 w-11/12 bg-sand-200 rounded mb-2" />
            <div className="h-4 w-2/3 bg-sand-200 rounded" />
        </section>
    );
}
